import React, { useRef, useState } from 'react';

// Request a Quote form.
// Delivery: POSTs JSON to /api/quote (droplet proxy -> Web3Forms -> gfox@gforcepromo.com).
// The Web3Forms key lives only on the server. Spam: honeypot + minimum fill time + server rate limit.
const FIELDS = [
  { id: 'name', label: 'Name', required: true, autoComplete: 'name' },
  { id: 'company', label: 'Company', autoComplete: 'organization' },
  { id: 'email', label: 'Email', type: 'email', required: true, autoComplete: 'email' },
  { id: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
  { id: 'occasion', label: 'Event or occasion', required: true },
  { id: 'date', label: 'Date needed', type: 'date' },
  { id: 'quantity', label: 'Quantity or budget range (optional)' },
  { id: 'message', label: 'Message', textarea: true, required: true },
];

const QuoteForm = ({ data = {} }) => {
  const started = useRef(Date.now());
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState('');
  const set = (id) => (e) => setValues({ ...values, [id]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (values.website) {
      setSent(true); // honeypot filled: silently drop bots
      return;
    }
    const errs = {};
    FIELDS.forEach((f) => {
      if (f.required && !(values[f.id] || '').trim()) errs[f.id] = 'Required';
    });
    if (values.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) errs.email = 'Please enter a valid email';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (Date.now() - started.current < 3000) {
      setSent(true); // filled faster than a person can: silently drop
      return;
    }
    setSending(true);
    setFailure('');
    try {
      const res = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...values, elapsed: (Date.now() - started.current) / 1000 }),
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok && out.ok) {
        setSent(true);
      } else {
        if (out.fields) setErrors(out.fields);
        setFailure(out.error || 'Sorry, your message didn’t go through. Please email gfox@gforcepromo.com or call 516-983-4963.');
      }
    } catch (err) {
      setFailure('Sorry, your message didn’t go through. Please email gfox@gforcepromo.com or call 516-983-4963.');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="gf-wrap gf-form-wrap" role="status">
        <div className="gf-thanks">
          <span className="gf-eyebrow">Thank you</span>
          <h2>{data.thanks_title || 'Thank you. I’ll be in touch soon.'}</h2>
          <p>{data.thanks_text || 'Your message is on its way to me. I’ll reply within one business day. If it’s urgent, call 516-983-4963.'}</p>
          <p className="gf-sign">Gail</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gf-wrap gf-form-wrap">
      <form className="gf-form" onSubmit={submit} noValidate>
        {FIELDS.map((f) => (
          <div key={f.id} className={`gf-field ${f.textarea ? 'gf-field-full' : ''} ${errors[f.id] ? 'has-error' : ''}`}>
            <label htmlFor={`gfq-${f.id}`}>{f.label}{f.required && <span aria-hidden="true"> *</span>}</label>
            {f.textarea ? (
              <textarea id={`gfq-${f.id}`} rows={6} value={values[f.id] || ''} onChange={set(f.id)} required={f.required} aria-invalid={!!errors[f.id]} />
            ) : (
              <input id={`gfq-${f.id}`} type={f.type || 'text'} value={values[f.id] || ''} onChange={set(f.id)} required={f.required} autoComplete={f.autoComplete} aria-invalid={!!errors[f.id]} />
            )}
            {errors[f.id] && <span className="gf-error">{errors[f.id]}</span>}
          </div>
        ))}
        <div className="gf-hp" aria-hidden="true">
          <label htmlFor="gfq-website">Website</label>
          <input id="gfq-website" type="text" tabIndex={-1} autoComplete="off" value={values.website || ''} onChange={set('website')} />
        </div>
        <div className="gf-field-full gf-form-foot">
          <button type="submit" className="gf-btn gf-btn-dark" disabled={sending}>{sending ? 'Sending…' : 'Send request'}</button>
          {failure && <p className="gf-form-alert" role="alert">{failure}</p>}
        </div>
      </form>
    </div>
  );
};
export default QuoteForm;
